import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Skeleton,
  Alert,
  Tooltip,
  IconButton,
  Collapse,
  InputAdornment,
  Divider,
  Tabs,
  Tab,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import HistoryIcon from "@mui/icons-material/History";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import TodayIcon from "@mui/icons-material/Today";
import LayersIcon from "@mui/icons-material/Layers";
import DownloadIcon from "@mui/icons-material/Download";
import LoginIcon from "@mui/icons-material/Login";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import api from "../../utils/api";
import StatCard from "../shared/StatCard";

/* ── Constants ──────────────────────────────────────────────────────────── */
const MODULE_OPTIONS = [
  "WorkOrders", "Violations", "ArchitecturalRequests", "Members",
  "Users", "Properties", "Reports", "SystemPreferences",
  "Dashboard", "Billing", "Documents", "Voting", "GateSecurity",
];

const ACTION_OPTIONS = [
  "CREATE", "UPDATE", "DELETE", "VIEW",
  "LOGIN", "LOGOUT", "EXPORT", "APPROVE",
  "REJECT", "ASSIGN", "COMPLETE", "REOPEN",
];

const ACTION_COLORS = {
  CREATE:   { bg: "rgba(46,125,50,0.10)",  color: "#2e7d32", border: "rgba(46,125,50,0.25)" },
  UPDATE:   { bg: "rgba(230,119,0,0.10)",  color: "#e65100", border: "rgba(230,119,0,0.25)" },
  DELETE:   { bg: "rgba(211,47,47,0.10)",  color: "#c62828", border: "rgba(211,47,47,0.25)" },
  VIEW:     { bg: "rgba(47,99,173,0.10)",  color: "#2f63ad", border: "rgba(47,99,173,0.25)" },
  LOGIN:    { bg: "rgba(106,27,154,0.10)", color: "#6a1b9a", border: "rgba(106,27,154,0.25)" },
  LOGOUT:   { bg: "rgba(84,110,122,0.10)", color: "#37474f", border: "rgba(84,110,122,0.25)" },
  EXPORT:   { bg: "rgba(0,131,143,0.10)",  color: "#00838f", border: "rgba(0,131,143,0.25)" },
  APPROVE:  { bg: "rgba(46,125,50,0.10)",  color: "#2e7d32", border: "rgba(46,125,50,0.25)" },
  REJECT:   { bg: "rgba(211,47,47,0.10)",  color: "#c62828", border: "rgba(211,47,47,0.25)" },
  ASSIGN:   { bg: "rgba(21,101,192,0.10)", color: "#1565c0", border: "rgba(21,101,192,0.25)" },
  COMPLETE: { bg: "rgba(46,125,50,0.10)",  color: "#2e7d32", border: "rgba(46,125,50,0.25)" },
  REOPEN:   { bg: "rgba(230,119,0,0.10)",  color: "#e65100", border: "rgba(230,119,0,0.25)" },
};

const DEFAULT_FILTERS = {
  search: "", module: "", action: "", userId: "", from: "", to: "",
};

const DEFAULT_LOGIN_FILTERS = {
  username: "", success: "", from: "", to: "",
};

/* ── Shared helpers ─────────────────────────────────────────────────────── */
const fmtDate = (iso) => {
  if (!iso) return "N/A";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    + " " + d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(255,255,255,0.96)",
    borderRadius: "10px",
  },
};

const filterClearBtnSx = {
  borderRadius: "10px", textTransform: "none", fontWeight: 600,
  borderColor: "rgba(47,99,173,0.25)", color: "#2f63ad",
};

const exportBtnSx = {
  borderRadius: "10px", textTransform: "none", fontWeight: 600,
  borderColor: "rgba(47,99,173,0.25)", color: "#2f63ad",
};

/* ── Shared sub-components ──────────────────────────────────────────────── */
const ActionChip = ({ action }) => {
  const style = ACTION_COLORS[action?.toUpperCase()] || ACTION_COLORS.VIEW;
  return (
    <Chip
      label={action || "N/A"}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: "0.72rem",
        height: 22,
        borderRadius: "6px",
        backgroundColor: style.bg,
        color: style.color,
        border: `1px solid ${style.border}`,
      }}
    />
  );
};

// SummaryCard wraps the shared StatCard for a consistent look across the app
const SummaryCard = ({ icon: Icon, label, value, color, loading }) => (
  <Box sx={{ flex: 1, minWidth: 160 }}>
    <StatCard
      icon={<Icon fontSize="small" />}
      title={label}
      count={loading ? <Skeleton width={50} /> : (value ?? "N/A")}
      backgroundColor={color}
    />
  </Box>
);

/* ── Expandable detail row (audit logs) ─────────────────────────────────── */
const LogDetailRow = ({ log, open }) => (
  <TableRow>
    <TableCell colSpan={7} sx={{ py: 0, px: 0, borderBottom: open ? undefined : "none" }}>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box
          sx={{
            mx: 2, my: 1.5, p: 1.75,
            borderRadius: "10px",
            backgroundColor: "rgba(47,99,173,0.04)",
            border: "1px solid rgba(47,99,173,0.10)",
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, color: "#2f63ad", mb: 0.5, display: "block" }}>
            Full Details
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0, fontSize: "0.78rem", color: "#334155",
              whiteSpace: "pre-wrap", wordBreak: "break-word",
              fontFamily: "monospace",
            }}
          >
            {log.details
              ? (typeof log.details === "string"
                  ? log.details
                  : JSON.stringify(log.details, null, 2))
              : "No additional details."}
          </Box>
        </Box>
      </Collapse>
    </TableCell>
  </TableRow>
);

/* ══════════════════════════════════════════════════════════════════════════
   AUDIT LOGS PANEL
══════════════════════════════════════════════════════════════════════════ */
const AuditLogsPanel = () => {
  const [logs, setLogs]             = useState([]);
  const [summary, setSummary]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [filters, setFilters]       = useState(DEFAULT_FILTERS);
  const [page, setPage]             = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRows, setTotalRows]   = useState(0);
  const [expandedRow, setExpandedRow] = useState(null);
  const debounceTimer = useRef(null);

  useEffect(() => {
    setSummaryLoading(true);
    api.get("/audit-logs/summary")
      .then((res) => setSummary(res.data))
      .catch((err) => console.error("Failed to load audit summary:", err))
      .finally(() => setSummaryLoading(false));
  }, []);

  const fetchLogs = useCallback(async (activeFilters, activePage, activeLimit) => {
    setLoading(true);
    setFetchError("");
    try {
      const params = new URLSearchParams();
      if (activeFilters.search)  params.set("search",  activeFilters.search.trim());
      if (activeFilters.module)  params.set("module",  activeFilters.module);
      if (activeFilters.action)  params.set("action",  activeFilters.action);
      if (activeFilters.userId)  params.set("userId",  activeFilters.userId.trim());
      if (activeFilters.from)    params.set("from",    activeFilters.from);
      if (activeFilters.to)      params.set("to",      activeFilters.to);
      params.set("page",  String(activePage + 1));
      params.set("limit", String(activeLimit));

      const { data } = await api.get(`/audit-logs?${params.toString()}`);
      setLogs(data.logs || data || []);
      setTotalRows(data.total ?? (data.logs?.length ?? 0));
    } catch (err) {
      console.error("Failed to fetch audit logs:", err);
      setFetchError("Failed to load audit logs. Please try again.");
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setPage(0);
      fetchLogs(filters, 0, rowsPerPage);
    }, 300);
    return () => clearTimeout(debounceTimer.current);
  }, [filters, rowsPerPage, fetchLogs]);

  useEffect(() => {
    fetchLogs(filters, page, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleFilterChange = (key) => (e) =>
    setFilters((prev) => ({ ...prev, [key]: e.target.value }));

  const clearFilters = () => { setFilters(DEFAULT_FILTERS); setPage(0); };

  const hasActiveFilters = Object.values(filters).some(Boolean);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.search)  params.set("search",  filters.search.trim());
      if (filters.module)  params.set("module",  filters.module);
      if (filters.action)  params.set("action",  filters.action);
      if (filters.userId)  params.set("userId",  filters.userId.trim());
      if (filters.from)    params.set("from",    filters.from);
      if (filters.to)      params.set("to",      filters.to);
      params.set("limit", "10000");
      params.set("page", "1");

      const { data } = await api.get(`/audit-logs?${params.toString()}`);
      const rows = data.logs || data || [];
      const headers = ["Timestamp", "User", "Module", "Action", "Description", "IP Address"];
      const csv = [
        headers.join(","),
        ...rows.map((r) =>
          [
            `"${fmtDate(r.timestamp || r.created_at)}"`,
            `"${r.username || r.userId || ""}"`,
            `"${r.module || ""}"`,
            `"${r.action || ""}"`,
            `"${(r.description || r.message || "").replace(/"/g, '""')}"`,
            `"${r.ipAddress || r.ip || ""}"`,
          ].join(",")
        ),
      ].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url  = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
    }
  };

  return (
    <Box>
      {/* Summary cards */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
        <SummaryCard icon={HistoryIcon}       label="Total Logs"    value={summary?.total?.toLocaleString()}               color="#2f63ad" loading={summaryLoading} />
        <SummaryCard icon={TodayIcon}         label="Today"         value={summary?.today?.toLocaleString()}               color="#16a34a" loading={summaryLoading} />
        <SummaryCard icon={PeopleOutlineIcon} label="Active Users"  value={summary?.uniqueUsers?.toLocaleString()}         color="#ea580c" loading={summaryLoading} />
        <SummaryCard icon={LayersIcon}        label="Top Module"    value={summary?.topModules?.[0]?.module || summary?.topModule || "N/A"} color="#7c3aed" loading={summaryLoading} />
      </Box>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: "14px", border: "1px solid rgba(47,99,173,0.08)", boxShadow: "0 4px 14px rgba(0,0,0,0.04)", backgroundColor: "rgba(255,255,255,0.96)" }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
          <TextField
            size="small" placeholder="Search logs…" value={filters.search}
            onChange={handleFilterChange("search")}
            sx={{ ...inputSx, minWidth: 200, flex: "1 1 200px" }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#9aa5b4" }} /></InputAdornment> }}
          />
          <FormControl size="small" sx={{ ...inputSx, minWidth: 160 }}>
            <InputLabel>Module</InputLabel>
            <Select value={filters.module} label="Module" onChange={handleFilterChange("module")}>
              <MenuItem value="">All Modules</MenuItem>
              {MODULE_OPTIONS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ ...inputSx, minWidth: 140 }}>
            <InputLabel>Action</InputLabel>
            <Select value={filters.action} label="Action" onChange={handleFilterChange("action")}>
              <MenuItem value="">All Actions</MenuItem>
              {ACTION_OPTIONS.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            size="small" placeholder="User ID or name…" value={filters.userId}
            onChange={handleFilterChange("userId")}
            sx={{ ...inputSx, minWidth: 160, flex: "1 1 160px" }}
          />
          <TextField size="small" type="date" label="From" value={filters.from} onChange={handleFilterChange("from")} InputLabelProps={{ shrink: true }} sx={{ ...inputSx, minWidth: 150 }} />
          <TextField size="small" type="date" label="To"   value={filters.to}   onChange={handleFilterChange("to")}   InputLabelProps={{ shrink: true }} sx={{ ...inputSx, minWidth: 150 }} />
          <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
            {hasActiveFilters && (
              <Tooltip title="Clear all filters">
                <Button size="small" variant="outlined" startIcon={<FilterListOffIcon />} onClick={clearFilters} sx={filterClearBtnSx}>
                  Clear
                </Button>
              </Tooltip>
            )}
            <Tooltip title="Export filtered logs to CSV">
              <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport} sx={exportBtnSx}>
                Export CSV
              </Button>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {fetchError && <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>{fetchError}</Alert>}

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: "14px", border: "1px solid rgba(47,99,173,0.08)", boxShadow: "0 4px 14px rgba(0,0,0,0.04)", overflow: "hidden", backgroundColor: "rgba(255,255,255,0.96)" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                {["", "Timestamp", "User", "Module", "Action", "Description", "IP Address"].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#5f6b7a", py: 1.25, borderBottom: "1px solid rgba(47,99,173,0.08)", whiteSpace: "nowrap", ...(h === "" && { width: 40, px: 1 }) }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton variant="circular" width={20} height={20} /></TableCell>
                    <TableCell><Skeleton width={130} /></TableCell>
                    <TableCell><Skeleton width={100} /></TableCell>
                    <TableCell><Skeleton width={110} /></TableCell>
                    <TableCell><Skeleton variant="rounded" width={70} height={22} /></TableCell>
                    <TableCell><Skeleton width={200} /></TableCell>
                    <TableCell><Skeleton width={90} /></TableCell>
                  </TableRow>
                ))
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: "center", py: 6 }}>
                    <HistoryIcon sx={{ fontSize: 40, color: "#d0d7e0", mb: 1, display: "block", mx: "auto" }} />
                    <Typography variant="body2" sx={{ color: "#9aa5b4" }}>
                      {hasActiveFilters ? "No logs match the current filters." : "No audit logs found."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log, idx) => {
                  const id   = log.id ?? log.log_id ?? idx;
                  const open = expandedRow === id;
                  const hasDetails = log.details || log.metadata;
                  return (
                    <React.Fragment key={id}>
                      <TableRow
                        hover
                        sx={{ cursor: hasDetails ? "pointer" : "default", "&:hover": { backgroundColor: "rgba(47,99,173,0.02)" }, borderLeft: open ? "3px solid #2f63ad" : "3px solid transparent" }}
                        onClick={() => hasDetails && setExpandedRow(open ? null : id)}
                      >
                        <TableCell sx={{ px: 1, py: 0.75 }}>
                          {hasDetails && (
                            <IconButton size="small" sx={{ p: 0.25 }}>
                              {open ? <ExpandLessIcon sx={{ fontSize: 16, color: "#2f63ad" }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: "#9aa5b4" }} />}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 1, whiteSpace: "nowrap", fontSize: "0.8rem", color: "#445" }}>
                          {fmtDate(log.timestamp || log.created_at || log.createdAt)}
                        </TableCell>
                        <TableCell sx={{ py: 1, fontSize: "0.82rem", fontWeight: 600, color: "#223142" }}>
                          {log.username || log.userName || log.userId || "N/A"}
                        </TableCell>
                        <TableCell sx={{ py: 1, fontSize: "0.82rem", color: "#445" }}>
                          {log.module || "N/A"}
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <ActionChip action={log.action} />
                        </TableCell>
                        <TableCell sx={{ py: 1, fontSize: "0.82rem", color: "#445", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          <Tooltip title={log.description || log.message || ""} placement="top">
                            <span>{log.description || log.message || "N/A"}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ py: 1, fontSize: "0.8rem", color: "#6b778c", fontFamily: "monospace" }}>
                          {log.ipAddress || log.ip || "N/A"}
                        </TableCell>
                      </TableRow>
                      {hasDetails && <LogDetailRow log={log} open={open} />}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Divider />
        <TablePagination
          component="div"
          count={totalRows}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: "none", "& .MuiTablePagination-toolbar": { px: 2 } }}
        />
      </Paper>
    </Box>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   LOGIN LOGS PANEL
══════════════════════════════════════════════════════════════════════════ */
const LoginLogsPanel = () => {
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [filters, setFilters] = useState(DEFAULT_LOGIN_FILTERS);
  const [limit, setLimit]     = useState(200);
  const debounceTimer = useRef(null);

  const fetchLogs = useCallback(async (activeFilters, activeLimit) => {
    setLoading(true);
    setFetchError("");
    try {
      const params = {};
      if (activeFilters.username) params.username = activeFilters.username.trim();
      if (activeFilters.success !== "") params.success = activeFilters.success;
      if (activeFilters.from) params.from = activeFilters.from;
      if (activeFilters.to)   params.to   = activeFilters.to;
      params.limit = activeLimit;

      const { data } = await api.get("/login-logs", { params });
      setLogs(data.logs || data || []);
    } catch (err) {
      console.error("Failed to fetch login logs:", err);
      setFetchError("Failed to load login logs. Please try again.");
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => fetchLogs(filters, limit), 300);
    return () => clearTimeout(debounceTimer.current);
  }, [filters, limit, fetchLogs]);

  const handleFilterChange = (key) => (e) =>
    setFilters((prev) => ({ ...prev, [key]: e.target.value }));

  const clearFilters = () => setFilters(DEFAULT_LOGIN_FILTERS);

  const hasActiveFilters = Object.values(filters).some(Boolean);

  /* Derived summary stats from loaded rows */
  const stats = React.useMemo(() => {
    const total    = logs.length;
    const success  = logs.filter((l) => l.success === 1 || l.success === true).length;
    const failed   = total - success;
    const unique   = new Set(logs.map((l) => l.username || l.user_id)).size;
    return { total, success, failed, unique };
  }, [logs]);

  const handleExport = () => {
    const headers = ["Timestamp", "Username", "Result", "IP Address", "User Agent"];
    const csv = [
      headers.join(","),
      ...logs.map((r) =>
        [
          `"${fmtDate(r.created_at || r.timestamp || r.logged_at)}"`,
          `"${r.username || r.user_id || ""}"`,
          `"${(r.success === 1 || r.success === true) ? "Success" : "Failed"}"`,
          `"${r.ip_address || r.ip || ""}"`,
          `"${(r.user_agent || "").replace(/"/g, '""')}"`,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `login-logs-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      {/* Summary cards */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
        <SummaryCard icon={LoginIcon}              label="Total Logins"    value={stats.total.toLocaleString()}   color="#2f63ad" loading={loading} />
        <SummaryCard icon={CheckCircleOutlineIcon} label="Successful"      value={stats.success.toLocaleString()} color="#16a34a" loading={loading} />
        <SummaryCard icon={ErrorOutlineIcon}       label="Failed"          value={stats.failed.toLocaleString()}  color="#dc2626" loading={loading} />
        <SummaryCard icon={PeopleOutlineIcon}      label="Unique Users"    value={stats.unique.toLocaleString()}  color="#ea580c" loading={loading} />
      </Box>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: "14px", border: "1px solid rgba(47,99,173,0.08)", boxShadow: "0 4px 14px rgba(0,0,0,0.04)", backgroundColor: "rgba(255,255,255,0.96)" }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
          <TextField
            size="small" placeholder="Search username…" value={filters.username}
            onChange={handleFilterChange("username")}
            sx={{ ...inputSx, minWidth: 200, flex: "1 1 200px" }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#9aa5b4" }} /></InputAdornment> }}
          />

          <FormControl size="small" sx={{ ...inputSx, minWidth: 150 }}>
            <InputLabel>Result</InputLabel>
            <Select value={filters.success} label="Result" onChange={handleFilterChange("success")}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="1">Successful</MenuItem>
              <MenuItem value="0">Failed</MenuItem>
            </Select>
          </FormControl>

          <TextField size="small" type="date" label="From" value={filters.from} onChange={handleFilterChange("from")} InputLabelProps={{ shrink: true }} sx={{ ...inputSx, minWidth: 150 }} />
          <TextField size="small" type="date" label="To"   value={filters.to}   onChange={handleFilterChange("to")}   InputLabelProps={{ shrink: true }} sx={{ ...inputSx, minWidth: 150 }} />

          <FormControl size="small" sx={{ ...inputSx, minWidth: 120 }}>
            <InputLabel>Limit</InputLabel>
            <Select value={limit} label="Limit" onChange={(e) => setLimit(e.target.value)}>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={200}>200</MenuItem>
              <MenuItem value={500}>500</MenuItem>
              <MenuItem value={1000}>1000</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
            {hasActiveFilters && (
              <Tooltip title="Clear all filters">
                <Button size="small" variant="outlined" startIcon={<FilterListOffIcon />} onClick={clearFilters} sx={filterClearBtnSx}>
                  Clear
                </Button>
              </Tooltip>
            )}
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={() => fetchLogs(filters, limit)} disabled={loading} sx={{ border: "1px solid rgba(47,99,173,0.25)", borderRadius: "10px", color: "#2f63ad" }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export to CSV">
              <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport} disabled={logs.length === 0} sx={exportBtnSx}>
                Export CSV
              </Button>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {fetchError && <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>{fetchError}</Alert>}

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: "14px", border: "1px solid rgba(47,99,173,0.08)", boxShadow: "0 4px 14px rgba(0,0,0,0.04)", overflow: "hidden", backgroundColor: "rgba(255,255,255,0.96)" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                {["Timestamp", "Username", "Result", "IP Address", "User Agent"].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#5f6b7a", py: 1.25, borderBottom: "1px solid rgba(47,99,173,0.08)", whiteSpace: "nowrap" }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton width={140} /></TableCell>
                    <TableCell><Skeleton width={110} /></TableCell>
                    <TableCell><Skeleton variant="rounded" width={72} height={22} /></TableCell>
                    <TableCell><Skeleton width={110} /></TableCell>
                    <TableCell><Skeleton width={220} /></TableCell>
                  </TableRow>
                ))
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: "center", py: 6 }}>
                    <LoginIcon sx={{ fontSize: 40, color: "#d0d7e0", mb: 1, display: "block", mx: "auto" }} />
                    <Typography variant="body2" sx={{ color: "#9aa5b4" }}>
                      {hasActiveFilters ? "No login logs match the current filters." : "No login logs found."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log, idx) => {
                  const isSuccess = log.success === 1 || log.success === true;
                  return (
                    <TableRow
                      key={log.id ?? idx}
                      hover
                      sx={{ "&:hover": { backgroundColor: "rgba(47,99,173,0.02)" }, "&:last-child td": { border: 0 } }}
                    >
                      {/* Timestamp */}
                      <TableCell sx={{ py: 1, whiteSpace: "nowrap", fontSize: "0.8rem", color: "#445" }}>
                        {fmtDate(log.created_at || log.timestamp || log.logged_at)}
                      </TableCell>

                      {/* Username */}
                      <TableCell sx={{ py: 1, fontSize: "0.82rem", fontWeight: 600, color: "#223142" }}>
                        {log.username || log.user_id || "N/A"}
                      </TableCell>

                      {/* Result chip */}
                      <TableCell sx={{ py: 1 }}>
                        <Chip
                          icon={isSuccess
                            ? <CheckCircleOutlineIcon sx={{ fontSize: "14px !important" }} />
                            : <ErrorOutlineIcon sx={{ fontSize: "14px !important" }} />}
                          label={isSuccess ? "Success" : "Failed"}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.72rem",
                            height: 22,
                            borderRadius: "6px",
                            backgroundColor: isSuccess ? "rgba(22,163,74,0.10)" : "rgba(220,38,38,0.10)",
                            color: isSuccess ? "#15803d" : "#dc2626",
                            border: `1px solid ${isSuccess ? "rgba(22,163,74,0.25)" : "rgba(220,38,38,0.25)"}`,
                            "& .MuiChip-icon": { color: isSuccess ? "#15803d" : "#dc2626" },
                          }}
                        />
                      </TableCell>

                      {/* IP */}
                      <TableCell sx={{ py: 1, fontSize: "0.8rem", color: "#6b778c", fontFamily: "monospace" }}>
                        {log.ip_address || log.ip || "N/A"}
                      </TableCell>

                      {/* User Agent. truncated */}
                      <TableCell sx={{ py: 1, maxWidth: 300 }}>
                        <Tooltip title={log.user_agent || ""} placement="top">
                          <Typography
                            variant="caption"
                            sx={{ color: "#6b778c", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          >
                            {log.user_agent || "N/A"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!loading && logs.length > 0 && (
          <>
            <Divider />
            <Box sx={{ px: 2, py: 1.25, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography variant="caption" sx={{ color: "#6b778c" }}>
                Showing {logs.length.toLocaleString()} record{logs.length !== 1 ? "s" : ""}
                {logs.length >= limit ? ` (limit ${limit}, increase limit or add filters to narrow results)` : ""}
              </Typography>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   ROOT COMPONENT
══════════════════════════════════════════════════════════════════════════ */
const AuditLogs = () => {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
          Audit Logs
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c" }}>
          Track every action taken across all modules. who did what and when.
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: 3,
          borderBottom: "1px solid #eef2f7",
          "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
        }}
      >
        <Tab label="Activity Logs" />
        <Tab
          label="Login Logs"
          icon={<LoginIcon sx={{ fontSize: 16 }} />}
          iconPosition="start"
        />
      </Tabs>

      {tab === 0 && <AuditLogsPanel />}
      {tab === 1 && <LoginLogsPanel />}
    </Box>
  );
};

export default AuditLogs;
